import { Button } from "@/components/atoms/Button";
import { PencilIcon } from "@/components/atoms/PencilIcon";
import { TextInput } from "@/components/atoms/TextInput";
import { Typography } from "@/components/atoms/Typography";
import { MailIcon, PhoneIcon, RefreshIcon } from "@/components/atoms/icons";
import { LogoHeader } from "@/components/molecules";
import { OTPInput } from "@/components/molecules/OTPInput";
import { PhoneInputRow } from "@/components/molecules/PhoneInputRow";
import { ProgressHeader } from "@/components/molecules/header/ProgressHeader";
import { useTranslation, useUIColors } from "@/core";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import { trackCatalogEvent } from "@/core/services/trackDynatraceCatalog";
import { logger } from "@/core/logger";
import { useI18nContext } from "@/core/providers/I18nProvider";
import {
  PHONE_NUMBER_MIN_LENGTH,
  formatPhoneNumberInput,
  isValidEmail,
  validatePhoneNumber,
} from "@/core/utils";
import {
  COUNTRIES,
  parsePhoneNumber as parsePhoneUtil,
  type Country,
} from "@/core/utils/countryCodes";
import { customerDetailsService } from "@/modules/onboarding/services/customerDetailsService";
import { usePathname, useRouter } from "expo-router";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

export interface OTPVerificationScreenProps {
  type: "phone" | "email";
  contactInfo?: string;
  progress?: number;
  otpLength?: number;
  mode?: "default" | "inline";
  onOTPComplete?:
    | ((otp: string) => void | boolean | Promise<void> | Promise<boolean>)
    | undefined;
  onResendOTP?: (() => void) | undefined;
  onBackPress?: (() => void) | undefined;
  onContactSubmit?: ((contact: string) => void) | undefined;
  onEditContact?: (() => void) | undefined; // Callback when edit button is clicked
  navigationTarget?: string;
  onOTPChange?: ((value: string) => void) | undefined;
  // Customization props for different use cases (e.g., forgot password)
  headerType?: "logo" | "progress";
  customIcon?: ReactNode;
  customTitle?: string;
  customDescription?: string;
  submitButtonText?: string;
  // Control when OTP field appears (for async API calls like forgot password, email-otp-inline, etc.)
  showOTP?: boolean;
  // Loading state for submit button (disabled during API calls)
  loading?: boolean;
  // Loading state for OTP verification (stops countdown, shows loading in OTP input)
  verifyingOTP?: boolean;
  // Error message for OTP verification (shows red border and enables resend)
  otpError?: string | null;
  // Success state for OTP verification (shows green border)
  otpSuccess?: boolean;
  // Verify-details API integration (optional - for registration flows)
  verifyDetailsConfig?: {
    customerId: string;
    registrationId: string;
  };
  // Callback after verify-details API succeeds (optional)
  onVerifyDetailsSuccess?: (type: "phone" | "email", contact: string) => void;
  // Update type for verify-otp API: Primary (default) or Secondary (for communication email from profile)
  updateType?: "Primary" | "Secondary";
}

export const OTPVerificationScreen = React.memo<OTPVerificationScreenProps>(
  function OTPVerificationScreen({
    type,
    contactInfo,
    progress = 0.6,
    otpLength = 4,
    mode = "default",
    onOTPComplete,
    onResendOTP,
    onBackPress,
    onContactSubmit,
    onEditContact,
    navigationTarget,
    onOTPChange,
    headerType = "progress",
    customIcon,
    customTitle,
    customDescription,
    submitButtonText,
    showOTP,
    loading = false,
    verifyingOTP = false,
    otpError,
    otpSuccess = false,
    verifyDetailsConfig,
    onVerifyDetailsSuccess,
    updateType = "Primary",
  }) {
    const MAX_RESEND_TIMER = 60;
    const router = useRouter();
    const pathname = usePathname();
    const colors = useUIColors();
    const { t } = useTranslation();
    const { isRTL } = useI18nContext();
    const insets = useSafeAreaInsets();

    // Check if opened from profile (profile routes start with /profile-)
    const isFromProfile = pathname?.includes("/profile-") || false;
    useCatalogDynatraceView(
      DT_SCREEN.OTP_INLINE,
      "inline_otp_viewed",
      mode === "inline",
    );
    const [resendTimer, setResendTimer] = useState(MAX_RESEND_TIMER);
    const [canResend, setCanResend] = useState(false);
    const [contactInput, setContactInput] = useState<string>(contactInfo || "");
    const [isContactSubmitted, setIsContactSubmitted] = useState<boolean>(
      !!contactInfo || !!showOTP,
    );
    const [otpValue, setOtpValue] = useState<string>("");
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [phoneError, setPhoneError] = useState<string>("");
    const [phoneTouched, setPhoneTouched] = useState<boolean>(false);

    // Parse phone number to extract country code and local number
    // Uses centralized country codes utility as single source of truth
    const parsePhone = useCallback((raw: string) => {
      return parsePhoneUtil(raw);
    }, []);

    // Parse contact input if it's a phone number
    const parsedPhone = useMemo(() => {
      if (type === "phone" && contactInput) {
        return parsePhone(contactInput.replace(/\s+/g, ""));
      }
      return { code: "+971", local: "" };
    }, [contactInput, type, parsePhone]);

    // Get selected country from parsed phone
    const selectedCountry = useMemo(() => {
      const country = COUNTRIES.find((c) => c.code === parsedPhone.code);
      return country || COUNTRIES[0]!; // COUNTRIES[0] is guaranteed to exist
    }, [parsedPhone.code]);

    // Initialize phone number from contactInfo if provided
    useEffect(() => {
      if (type === "phone" && contactInfo) {
        const parsed = parsePhone(contactInfo.replace(/\s+/g, ""));
        setPhoneNumber(parsed.local);
      }
    }, [contactInfo, type, parsePhone]);

    // Update isContactSubmitted when showOTP prop changes (for async API calls)
    // Also reset resend timer when showOTP becomes false (when edit is pressed)
    useEffect(() => {
      if (showOTP !== undefined) {
        setIsContactSubmitted(showOTP);
        // Reset resend timer when OTP is hidden (edit button pressed)
        if (!showOTP) {
          setResendTimer(MAX_RESEND_TIMER);
          setCanResend(false);
        }
      }
    }, [showOTP]);

    // Stop countdown when verifying OTP, but continue even after error
    useEffect(() => {
      // Don't run countdown if verifying OTP
      if (verifyingOTP) {
        return;
      }

      // Continue countdown even after error - only enable resend when timer reaches 0
      if (resendTimer > 0) {
        const timer = setTimeout(() => {
          setResendTimer(resendTimer - 1);
        }, 1000);
        return () => clearTimeout(timer);
      } else {
        setCanResend(true);
      }
      return undefined;
    }, [resendTimer, verifyingOTP]);

    // Clear OTP value when error occurs (to allow re-entry)
    useEffect(() => {
      if (otpError) {
        setOtpValue("");
      }
    }, [otpError]);

    const handleOTPComplete = async (value: string) => {
      // Set the value immediately
      setOtpValue(value);
      if (mode === "inline") {
        trackCatalogEvent({
          screen: DT_SCREEN.OTP_INLINE,
          actionName: "inline_otp_verify_submit",
          eventType: "action",
        });
      }

      // Support async onOTPComplete callback - this will trigger loading state immediately
      if (onOTPComplete) {
        const result = await Promise.resolve(onOTPComplete(value));
        // Check if result is a boolean (verification result), otherwise treat as void
        const verified = typeof result === "boolean" ? result : true;

        // If OTP verification succeeded and verify-details config is provided, call verify-details API
        if (verified && verifyDetailsConfig) {
          const contactToUse = contactInfo || contactInput;
          if (!contactToUse) {
            logger.warn(
              "OTPVerificationScreen",
              "No contact info available for verify-details API",
            );
            onVerifyDetailsSuccess?.(type, "");
            return;
          }

          try {
            logger.info(
              "OTPVerificationScreen",
              "OTP verified, calling verify-details API",
              {
                type,
                customerId: verifyDetailsConfig.customerId,
                contact: contactToUse,
              },
            );

            let request: {
              customer_id: string;
              registration_id: string;
              country_code?: string;
              mobile?: string;
              email?: string;
              update_type?: "Primary" | "Secondary";
            } = {
              customer_id: verifyDetailsConfig.customerId,
              registration_id: verifyDetailsConfig.registrationId,
              update_type: updateType,
            };

            if (type === "phone") {
              // Parse phone number to extract country code and mobile number
              const phoneStr = contactToUse.replace(/\s+/g, "");
              let countryCode = "971"; // Default to UAE
              let mobile = phoneStr;

              // Extract country code if phone starts with +
              if (phoneStr.startsWith("+")) {
                const codeMatch = /^\+\d{1,3}/.exec(phoneStr);
                if (codeMatch?.[0]) {
                  countryCode = codeMatch[0].slice(1); // Remove + sign
                  mobile = phoneStr.replace(/^\+\d{1,3}/, "");
                }
              } else {
                // Try to match known country codes
                const knownCodes = [
                  "971",
                  "1",
                  "44",
                  "33",
                  "49",
                  "39",
                  "34",
                  "86",
                  "81",
                  "82",
                  "91",
                  "61",
                  "55",
                  "52",
                  "7",
                  "27",
                ];
                const matchedCode = knownCodes.find((cc) =>
                  phoneStr.startsWith(cc),
                );
                if (matchedCode) {
                  countryCode = matchedCode;
                  mobile = phoneStr.slice(matchedCode.length);
                }
              }

              request.country_code = countryCode;
              request.mobile = mobile;
            } else {
              // Email type
              request.email = contactToUse;
            }

            await customerDetailsService.verifyDetails(request);
            logger.info(
              "OTPVerificationScreen",
              "Verify-details API called successfully",
            );
            onVerifyDetailsSuccess?.(type, contactToUse);
          } catch (error) {
            logger.error(
              "OTPVerificationScreen",
              "Failed to call verify-details API",
              error,
            );
            // Still call success callback even if API fails (for navigation)
            onVerifyDetailsSuccess?.(type, contactToUse || "");
          }
        }
      }

      if (navigationTarget) {
        // Navigate after showing success state
        setTimeout(() => {
          router.push(navigationTarget as any);
        }, 1000);
      }
    };

    const handleOTPChange = (value: string) => {
      setOtpValue(value);
      // Notify parent when OTP changes (for error clearing)
      onOTPChange?.(value);
    };

    useEffect(() => {
      if (otpSuccess) {
        Keyboard.dismiss();
      }
    }, [otpSuccess]);

    const handleResendPress = () => {
      if (canResend) {
        if (mode === "inline") {
          trackCatalogEvent({
            screen: DT_SCREEN.OTP_INLINE,
            actionName: "inline_otp_resend_click",
            eventType: "action",
          });
        }
        setResendTimer(MAX_RESEND_TIMER);
        setCanResend(false);
        onResendOTP?.();
      }
    };

    const handleEditContact = () => {
      // Notify parent to reset showOTP state
      onEditContact?.();
      // Reset contact submission state to allow editing
      setIsContactSubmitted(false);
      // Clear OTP value
      setOtpValue("");
      // Reset countdown timer
      setResendTimer(MAX_RESEND_TIMER);
      setCanResend(false);
      // Clear any errors and touched state
      setPhoneError("");
      setPhoneTouched(false);
      onOTPChange?.("");
    };

    const handleBackPress = () => {
      if (onBackPress) {
        onBackPress();
      } else {
        // Check if we can go back before attempting navigation
        if (router.canGoBack()) {
          router.back();
        } else {
          logger.warn(
            "OTPVerificationScreen",
            "Cannot go back - no previous screen",
          );
        }
      }
    };

    const handleContactChange = (value: string) => {
      if (type === "phone") {
        // Mark as touched when user starts typing
        if (!phoneTouched) {
          setPhoneTouched(true);
        }
        // For phone, format input to enforce max length and remove non-digits
        const formatted = formatPhoneNumberInput(value);
        setPhoneNumber(formatted);
        // Validate in real-time and show error if invalid
        const localNumber = formatted;
        if (localNumber.length > 0) {
          const validationError = validatePhoneNumber(localNumber);
          setPhoneError(validationError);
        } else {
          // Show required error if field is empty and user has interacted with it
          if (phoneTouched) {
            setPhoneError("Phone number is required");
          } else {
            setPhoneError("");
          }
        }
        // Update contactInput with full phone number (country code + local)
        const fullPhone = `${selectedCountry?.code.replace("+", "") || "+971"}${formatted}`;
        setContactInput(fullPhone);
      } else {
        setContactInput(value);
      }
    };

    const handleCountrySelect = useCallback(
      (country: Country) => {
        // Update contactInput with new country code + existing local number
        const localNumber = phoneNumber || parsedPhone.local;
        const fullPhone = `${country.code.replace("+", "")}${localNumber}`;
        setContactInput(fullPhone);
        // Re-validate phone number when country changes
        if (type === "phone" && localNumber.length > 0) {
          const validationError = validatePhoneNumber(localNumber);
          setPhoneError(validationError);
        }
      },
      [phoneNumber, parsedPhone.local, type],
    );

    const handleConfirmContact = () => {
      if (!contactInput.trim()) {
        return;
      }

      // Validate based on type
      if (type === "email") {
        // Validate email format
        if (!isValidEmail(contactInput)) {
          return; // Invalid email, don't submit
        }
      } else if (type === "phone") {
        // Validate phone number
        const localNumber = phoneNumber || parsedPhone.local;
        const validationError = validatePhoneNumber(localNumber);
        if (validationError) {
          setPhoneError(validationError);
          return; // Invalid phone, don't submit
        }
        setPhoneError(""); // Clear error if valid
      }

      // For phone, ensure we have the full phone number with country code (with + sign)
      let finalContact = contactInput.trim();
      if (type === "phone" && phoneNumber && selectedCountry) {
        finalContact = `${selectedCountry.code}${phoneNumber}`;
      }
      // If showOTP prop is provided, parent controls when to show OTP
      // Otherwise, set isContactSubmitted immediately (for sync flows)
      if (showOTP === undefined) {
        setIsContactSubmitted(true);
      }
      onContactSubmit?.(finalContact);
    };

    const getIcon = () => {
      if (customIcon) return customIcon;
      return type === "phone" ? (
        <PhoneIcon height={33} width={33} color={colors.fontPrimary} />
      ) : (
        <MailIcon height={26} width={33} color={colors.fontPrimary} />
      );
    };

    const getTitle = () => {
      if (customTitle) return customTitle;

      // If opened from profile, use different titles based on type and updateType
      if (isFromProfile) {
        if (type === "phone") {
          return t("otp.changePhoneNumber");
        } else {
          // Email type
          if (updateType === "Primary") {
            return t("otp.changeLoginEmail");
          } else {
            // Secondary (communication email)
            return t("otp.changeCommunicationEmail");
          }
        }
      }

      // Default titles for registration flows
      return type === "phone"
        ? t("otp.verifyPhoneNumber")
        : t("otp.verifyEmail");
    };

    const getSubtitle = () => {
      if (customDescription) return customDescription;
      if (mode === "inline") {
        return type === "phone"
          ? t("otp.enterPhoneForOTP")
          : t("otp.enterEmailForOTP");
      }
      // default mode
      return t("otp.enterOTP").replace("{contact}", contactInfo ?? "");
    };

    const getExtraDescription = () => {
      return type === "phone"
        ? t("otp.verifyPhoneDescription")
        : t("otp.verifyEmailDescription");
    };

    const renderHeader = () => {
      if (headerType === "logo") {
        return <LogoHeader showLanguageSwitch={!isFromProfile} />;
      }
      // When from profile, hide progress bar and language switch
      if (isFromProfile) {
        return (
          <ProgressHeader
            progress={progress}
            onBackPress={handleBackPress}
            showLanguageSwitch={false}
            hideProgressBar={true}
            hideBackIfRoot={true}
          />
        );
      }
      return (
        <ProgressHeader
          progress={progress}
          onBackPress={handleBackPress}
          showLanguageSwitch={true}
          hideProgressBar={false}
          hideBackIfRoot={true}
        />
      );
    };

    return (
      <View
        style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}
      >
        {renderHeader()}

        <KeyboardAvoidingView
          style={styles.keyboardAvoid}
          behavior={Platform.OS === "ios" ? "padding" : "height"}
        >
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="on-drag"
          >
            <View style={styles.content}>
              {/* Header Section */}
              <View style={styles.headerSection}>
                <View style={styles.iconContainer}>{getIcon()}</View>

                <Typography
                  variant="title1"
                  color={colors.fontPrimary}
                  style={styles.title}
                >
                  {getTitle()}
                </Typography>
                {mode === "default" && (
                  <Typography
                    variant="body"
                    color={colors.fontSecondary}
                    style={styles.extraDescription}
                  >
                    {getExtraDescription()}
                  </Typography>
                )}
                <Typography
                  variant="body"
                  color={colors.fontSecondary}
                  style={styles.subtitle}
                >
                  {getSubtitle()}
                </Typography>
              </View>

              {/* Contact Input Section - Always visible in inline mode */}
              {mode === "inline" && (
                <View style={styles.contactSection}>
                  {type === "phone" ? (
                    <View style={styles.phoneInputContainer}>
                      <Typography
                        variant="footnote"
                        color="secondary"
                        style={[
                          styles.phoneLabel,
                          { textAlign: Platform.OS === "web" && isRTL ? "right" : "left" },                        ]}
                      >
                        {t("profile.phoneNumber")} *
                      </Typography>
                      <PhoneInputRow
                        selectedCountry={selectedCountry}
                        phoneNumber={phoneNumber || parsedPhone.local}
                        onCountrySelect={handleCountrySelect}
                        onPhoneChange={handleContactChange}
                        disabled={isContactSubmitted}
                        error={phoneError || ""}
                        placeholder={t("profile.enterPhoneNumber")}
                      />
                    </View>
                  ) : (
                    <View
                      style={
                        Platform.OS === "web" && isRTL
                          ? { direction: "ltr", width: "100%" }
                          : undefined
                      }
                    >
                      <TextInput
                        label={
                          customTitle
                            ? t("auth.emailId")
                            : t("profile.emailRequired")
                        }
                        placeholder={
                          customTitle
                            ? t("auth.enterEmailId")
                            : t("profile.enterEmailAddress")
                        }
                        value={contactInput}
                        onChangeText={handleContactChange}
                        keyboardType="email-address"
                        autoCapitalize="none"
                        autoComplete="email"
                        textContentType="emailAddress"
                        leftIcon={
                          <MailIcon size={20} color={colors.fontTertiary} />
                        }
                        disabled={isContactSubmitted}
                        required
                        rightIcon={
                          isContactSubmitted ? (
                            <Pressable
                              onPress={handleEditContact}
                              disabled={verifyingOTP}
                            >
                              <PencilIcon
                                size={20}
                                color={colors.fontSecondary}
                              />
                            </Pressable>
                          ) : undefined
                        }
                      />
                    </View>
                  )}
                </View>
              )}

              {/* OTP Section - Only visible after contact is submitted in inline mode */}
              {mode === "inline" && isContactSubmitted && (
                <View style={styles.otpSection}>
                  <OTPInput
                    length={otpLength}
                    onComplete={handleOTPComplete}
                    onChangeText={handleOTPChange}
                    value={otpValue}
                    loading={verifyingOTP}
                    type={type}
                    {...(otpError ? { errorMessage: otpError } : {})}
                    {...(otpSuccess
                      ? { successMessage: t("otp.otpVerified") }
                      : {})}
                    autoFocus={!otpSuccess && !verifyingOTP}
                    focusable={!otpSuccess && !verifyingOTP}
                    disabled={verifyingOTP}
                  />

                  {/* Resend Button */}
                  {!otpSuccess && (
                    <View style={styles.resendContainer}>
                      {canResend ? (
                        <Button
                          title={t("otp.resendOTP")}
                          variant="text"
                          onPress={handleResendPress}
                          leading={<RefreshIcon size={19} />}
                          disabled={verifyingOTP}
                        />
                      ) : (
                        <Typography
                          color="fontSecondary"
                          style={{
                            fontSize: 16,
                            fontWeight: "600",
                            paddingVertical: 14,
                          }}
                        >
                          {t("otp.resendOTPTimer").replace(
                            "{resendTimer}",
                            resendTimer.toString(),
                          )}
                        </Typography>
                      )}
                    </View>
                  )}
                </View>
              )}

              {/* Default OTP Section - Only for default mode */}
              {mode === "default" && (
                <View style={styles.otpSection}>
                  <OTPInput
                    length={otpLength}
                    onComplete={handleOTPComplete}
                    onChangeText={handleOTPChange}
                    value={otpValue}
                    loading={verifyingOTP}
                    type={type}
                    {...(otpError ? { errorMessage: otpError } : {})}
                    {...(otpSuccess
                      ? { successMessage: t("otp.otpVerified") }
                      : {})}
                    autoFocus={!otpSuccess && !verifyingOTP}
                    focusable={!otpSuccess && !verifyingOTP}
                    disabled={verifyingOTP}
                  />

                  {/* Resend Button */}
                  {!otpSuccess && (
                    <View style={styles.resendContainer}>
                      {canResend || otpError ? (
                        <Button
                          title={t("otp.resendOTP")}
                          variant="text"
                          onPress={handleResendPress}
                          leading={<RefreshIcon size={19} />}
                          disabled={verifyingOTP}
                        />
                      ) : (
                        <Typography
                          color="fontSecondary"
                          style={{
                            fontSize: 16,
                            fontWeight: "600",
                            paddingVertical: 14,
                          }}
                        >
                          {t("otp.resendOTPTimer").replace(
                            "{resendTimer}",
                            resendTimer.toString(),
                          )}
                        </Typography>
                      )}
                    </View>
                  )}
                </View>
              )}
            </View>
          </ScrollView>
        </KeyboardAvoidingView>

        {/* Confirm Button - Outside KeyboardAvoidingView to prevent positioning issues */}
        {mode === "inline" && !isContactSubmitted && (
          <View
            style={[
              styles.bottomButtonContainer,
              { bottom: insets.bottom + 35 },
            ]}
          >
            <Button
              title={submitButtonText || t("otp.requestOtp")}
              fullWidth={true}
              onPress={handleConfirmContact}
              disabled={
                loading ||
                !contactInput.trim() ||
                (type === "email" && !isValidEmail(contactInput)) ||
                (type === "phone" &&
                  (!!phoneError ||
                    !phoneNumber ||
                    phoneNumber.length < PHONE_NUMBER_MIN_LENGTH))
              }
            />
          </View>
        )}
      </View>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  keyboardAvoid: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 32,
  },
  headerSection: {
    alignItems: "flex-start",
    marginBottom: 32,
  },
  iconContainer: {
    marginBottom: 24,
  },
  title: {
    marginBottom: 16,
    fontSize: 28,
    fontWeight: "500",
  },
  subtitle: {
    fontSize: 16,
    lineHeight: 24,
  },
  extraDescription: {
    fontSize: 16,
    lineHeight: 24,
    marginBottom: 16,
  },
  otpSection: {
    paddingVertical: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  resendContainer: {
    paddingVertical: 12,
    marginTop: 16,
    alignItems: "center",
  },
  contactSection: {
    paddingVertical: 0,
    gap: 32,
    marginBottom: 32,
  },
  phoneInputContainer: {
    gap: 8,
  },
  phoneLabel: {
    fontSize: 13,
    lineHeight: 16,
  },
  phoneRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  phoneInputWrapper: {
    flex: 1,
  },
  phoneInput: {
    flex: 1,
  },
  phoneErrorContainer: {
    marginTop: 4,
    paddingLeft: 122, // Country code dropdown minWidth (110) + gap (12) to align with phone input
  },
  phoneErrorText: {
    fontSize: 12,
  },
  bottomButtonContainer: {
    position: "absolute",
    left: 16,
    right: 16,
    zIndex: 10,
  },
});

export default OTPVerificationScreen;
